import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { DealCategoryTreeNode, useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { findCategoryPath, isSelectableDepth, searchCategories } from './category-tree-helpers';
import type { CategoryRef } from './editable-deal-row.types';
import { GridCategoryCascade } from './grid-category-cascade';

export interface IGridCategoryModalProps {
  trigger: JSX.Element;
  value: CategoryRef | null;
  onSelect: (category: CategoryRef) => void;
}

export const GridCategoryModal = ({ trigger, value, onSelect }: IGridCategoryModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, isLoading } = useGetDealCategoryTreeApi({ enabled: isOpen });
  const tree = data?.data ?? [];
  const [selectedPath, setSelectedPath] = useState<DealCategoryTreeNode[]>([]);
  const [query, setQuery] = useState('');
  const hasPreselected = useRef(false);

  // Reset when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setSelectedPath([]);
      setQuery('');
      hasPreselected.current = false;
    }
  }, [isOpen]);

  // Pre-select the current category's path exactly once, when the tree first arrives after open.
  // Guarded so a later re-render (new value/tree reference) never overwrites the user's drilling.
  useEffect(() => {
    if (isOpen && value && tree.length && !hasPreselected.current) {
      hasPreselected.current = true;
      setSelectedPath(findCategoryPath(tree, value.id));
    }
  }, [isOpen, value, tree]);

  const leaf = selectedPath[selectedPath.length - 1];
  const isValidLeaf = !!leaf && isSelectableDepth(leaf.depth);

  // Client-side search over the already-fetched tree; picking a hit reveals
  // its whole lineage in the cascade (the hit's path IS a selectedPath).
  const isSearching = query.trim().length > 0;
  const searchHits = useMemo(() => (isSearching ? searchCategories(tree, query) : []), [tree, query, isSearching]);

  const pickSearchHit = (path: DealCategoryTreeNode[]) => {
    setSelectedPath(path);
    setQuery('');
  };

  const handleConfirm = () => {
    if (!isValidLeaf || !leaf) return;
    onSelect({ id: leaf.id, title: leaf.title });
    onClose();
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent maxW='1080px'>
          <ModalHeader>Select Product Category</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <HStack justify='center' py='lg'>
                <Spinner size='md' />
                <Text color='gray.500'>Loading categories...</Text>
              </HStack>
            ) : tree.length === 0 ? (
              <Box py='lg' px='md' bg='gray.50' borderRadius='md'>
                <Text color='gray.500' textAlign='center'>
                  No categories available.
                </Text>
              </Box>
            ) : (
              <VStack align='stretch' spacing='sm'>
                <Input
                  size='sm'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Search categories by name…'
                />
                {isSearching ? (
                  searchHits.length === 0 ? (
                    <Box py='lg' px='md' bg='gray.50' borderRadius='md'>
                      <Text color='gray.500' textAlign='center'>
                        No categories match “{query.trim()}”.
                      </Text>
                    </Box>
                  ) : (
                    <VStack
                      align='stretch'
                      spacing={0}
                      height='420px'
                      overflowY='auto'
                      borderWidth='1px'
                      borderColor='gray.200'
                      borderRadius='md'>
                      {searchHits.map(({ node, path }) => (
                        <HStack
                          key={node.id}
                          as='button'
                          type='button'
                          onClick={() => pickSearchHit(path)}
                          justify='space-between'
                          px='sm'
                          py='xs'
                          bg='white'
                          _hover={{ bg: 'gray.50' }}
                          cursor='pointer'>
                          <VStack align='start' spacing={0} minW={0}>
                            <Text fontSize='sm' fontWeight='medium' textAlign='left' noOfLines={1}>
                              {node.title || `Untitled (${node.id})`}
                            </Text>
                            <Text fontSize='xs' color='gray.500' textAlign='left' noOfLines={1}>
                              {path.map((p) => p.title || `Untitled (${p.id})`).join(' › ')}
                            </Text>
                          </VStack>
                          <Badge
                            colorScheme={isSelectableDepth(node.depth) ? 'red' : 'gray'}
                            variant='subtle'
                            fontSize='10px'
                            flexShrink={0}>
                            {isSelectableDepth(node.depth) ? 'Selectable' : `Depth ${node.depth}`}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  )
                ) : (
                  <>
                    <HStack justify='space-between' minH='28px'>
                      <Button
                        variant='ghost'
                        size='xs'
                        onClick={() => setSelectedPath(selectedPath.slice(0, -1))}
                        isDisabled={selectedPath.length === 0}>
                        Back
                      </Button>
                      {selectedPath.length > 0 && (
                        <Button variant='link' size='xs' colorScheme='gray' onClick={() => setSelectedPath([])}>
                          Reset
                        </Button>
                      )}
                    </HStack>
                    <GridCategoryCascade tree={tree} selectedPath={selectedPath} onSelect={setSelectedPath} />
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack width='100%' justify='space-between' align='center' spacing='md'>
              <VStack align='start' spacing={0} flex='1' minW={0}>
                {selectedPath.length > 0 ? (
                  <Text fontSize='sm' color='gray.700' noOfLines={1}>
                    {selectedPath.map((p) => p.title || `Untitled (${p.id})`).join(' › ')}
                  </Text>
                ) : (
                  <Text fontSize='sm' color='gray.400'>
                    Pick a category to see the path here.
                  </Text>
                )}
                {selectedPath.length > 0 && !isValidLeaf && (
                  <Text fontSize='xs' color='gray.500'>
                    Pick a category at depth 2, 3 or 4 to confirm.
                  </Text>
                )}
              </VStack>
              <HStack spacing='sm'>
                <Button variant='outline' colorScheme='gray' onClick={onClose}>
                  Cancel
                </Button>
                <Button variant='solid' colorScheme='red' onClick={handleConfirm} isDisabled={!isValidLeaf}>
                  Select
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
